import React from 'react';
import { ImageOverlay, Pane, useMap } from 'react-leaflet';

import { DATED_RASTER_PANE, DATED_RASTER_PANE_Z_INDEX } from './datedRasterShared';
import styles from './DatedRasterLayer.module.css';
import { useFmDatedRasterCompare } from '@/components/ui/FmDatedRasterCompareContext';
import type { FmDatedRasterOverlay } from '@/shared/config/mapSlotContracts';

interface DatedRasterLayerProps {
  overlay: FmDatedRasterOverlay | null | undefined;
}

const EARLIER_PANE = `${DATED_RASTER_PANE}-earlier`;
const LATER_PANE = `${DATED_RASTER_PANE}-later`;

function wgs84ImageBounds(
  bounds: readonly [number, number, number, number],
): [[number, number], [number, number]] | null {
  const [west, south, east, north] = bounds;
  if (
    !Number.isFinite(west) ||
    !Number.isFinite(south) ||
    !Number.isFinite(east) ||
    !Number.isFinite(north) ||
    west >= east ||
    south >= north ||
    Math.abs(west) > 180 ||
    Math.abs(east) > 180 ||
    Math.abs(south) > 90 ||
    Math.abs(north) > 90
  ) {
    return null;
  }
  return [
    [south, west],
    [north, east],
  ];
}

function visibleRevealClip(map: ReturnType<typeof useMap>, reveal: number): string {
  const laterPane = map.getPane(LATER_PANE);
  const image = laterPane?.querySelector('img');
  const container = map.getContainer();
  if (!image || !container) return `${reveal}%`;
  const mapBox = container.getBoundingClientRect();
  const imageBox = image.getBoundingClientRect();
  if (imageBox.width < 1) return `${reveal}%`;
  const seamX = mapBox.left + (mapBox.width * reveal) / 100;
  const clip = ((seamX - imageBox.left) / imageBox.width) * 100;
  return `${Math.min(100, Math.max(0, clip))}%`;
}

function DatedRasterRevealClip() {
  const map = useMap();
  const compare = useFmDatedRasterCompare();
  const reveal = compare?.reveal;

  React.useEffect(() => {
    if (reveal == null) {
      throw new Error('Dated raster reveal clip requires Analysis compare context.');
    }
    const applyClip = () => {
      const laterPane = map.getPane(LATER_PANE);
      const image = laterPane?.querySelector('img');
      if (!image) return;
      image.style.clipPath = `inset(0 0 0 ${visibleRevealClip(map, reveal)})`;
    };
    applyClip();
    const image = map.getPane(LATER_PANE)?.querySelector('img');
    image?.addEventListener('load', applyClip);
    map.on('move', applyClip);
    map.on('zoom', applyClip);
    map.on('resize', applyClip);
    return () => {
      image?.removeEventListener('load', applyClip);
      map.off('move', applyClip);
      map.off('zoom', applyClip);
      map.off('resize', applyClip);
    };
  }, [map, reveal]);

  return null;
}

function RasterImage({
  url,
  bounds,
  pane,
  className,
}: {
  url: string;
  bounds: [[number, number], [number, number]];
  pane: string;
  className?: string;
}) {
  return (
    <ImageOverlay
      url={url}
      bounds={bounds}
      pane={pane}
      className={className}
      crossOrigin="anonymous"
    />
  );
}

/** Property-clipped dated imagery. A pair renders only with Analysis compare context. */
const DatedRasterLayer: React.FC<DatedRasterLayerProps> = ({ overlay }) => {
  const compare = useFmDatedRasterCompare();
  if (overlay?.status !== 'ready') return null;
  const laterBounds = wgs84ImageBounds(overlay.bounds);
  if (!laterBounds) return null;
  const earlier = overlay.comparedWith && compare ? overlay.comparedWith : null;
  const earlierBounds = earlier ? wgs84ImageBounds(earlier.bounds) : null;
  const pair = Boolean(earlier && earlierBounds);

  return (
    <>
      {pair && earlier && earlierBounds ? (
        <Pane
          name={EARLIER_PANE}
          className={styles.pane}
          style={{ zIndex: DATED_RASTER_PANE_Z_INDEX, pointerEvents: 'none' }}
        >
          <RasterImage url={earlier.imageUrl} bounds={earlierBounds} pane={EARLIER_PANE} />
        </Pane>
      ) : null}
      <Pane
        name={LATER_PANE}
        className={styles.pane}
        style={{
          zIndex: DATED_RASTER_PANE_Z_INDEX + (pair ? 1 : 0),
          pointerEvents: 'none',
        }}
      >
        <RasterImage url={overlay.imageUrl} bounds={laterBounds} pane={LATER_PANE} />
      </Pane>
      {pair ? <DatedRasterRevealClip /> : null}
    </>
  );
};

export default DatedRasterLayer;
